import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Users,
  Search,
  Edit,
  Euro,
  Briefcase,
  CheckCircle,
  XCircle,
} from 'lucide-react';

export default function ConsultantsPage() {
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const [editData, setEditData] = useState({
    skills: '',
    tjm: '',
    availability: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await api.get('/consultants');
      setConsultants(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (consultant) => {
    setSelectedConsultant(consultant);
    setEditData({
      skills: consultant.skills?.join(', ') || '',
      tjm: consultant.tjm?.toString() || '',
      availability: consultant.availability ?? true,
    });
    setIsEditDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const params = new URLSearchParams();
      if (editData.skills) {
        const skillsArray = editData.skills.split(',').map(s => s.trim()).filter(Boolean);
        skillsArray.forEach(skill => params.append('skills', skill));
      }
      if (editData.tjm) {
        params.append('tjm', editData.tjm);
      }
      params.append('availability', editData.availability);

      await api.put(`/consultants/${selectedConsultant.consultant_id}?${params.toString()}`);
      toast.success('Consultant mis à jour');
      setIsEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const filteredConsultants = consultants.filter(c =>
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.skills?.some(s => s.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const availableCount = consultants.filter(c => c.availability).length;
  const avgTJM = consultants.length > 0
    ? Math.round(consultants.reduce((sum, c) => sum + (c.tjm || 0), 0) / consultants.length)
    : 0;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Consultants</h1>
            <p className="text-slate-500 mt-1">
              Gérez votre équipe de consultants
            </p>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher un consultant..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-64"
              data-testid="search-consultants"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{consultants.length}</p>
                  <p className="text-sm text-slate-500">Total consultants</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{availableCount}</p>
                  <p className="text-sm text-slate-500">Disponibles</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Euro className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{avgTJM} €</p>
                  <p className="text-sm text-slate-500">TJM moyen</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Consultants Table */}
        <Card>
          <CardHeader>
            <CardTitle>Liste des consultants</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredConsultants.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucun consultant trouvé</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Consultant</TableHead>
                      <TableHead>Compétences</TableHead>
                      <TableHead className="text-center">TJM</TableHead>
                      <TableHead className="text-center">Disponibilité</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredConsultants.map((consultant) => (
                      <TableRow key={consultant.consultant_id} data-testid={`consultant-row-${consultant.consultant_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className="bg-slate-900 text-white">
                                {getInitials(consultant.name)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium text-slate-900">{consultant.name}</p>
                              <p className="text-sm text-slate-500">{consultant.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {consultant.skills?.slice(0, 3).map((skill, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">
                                {skill}
                              </Badge>
                            ))}
                            {consultant.skills?.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{consultant.skills.length - 3}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center font-mono">
                          {consultant.tjm > 0 ? `${consultant.tjm} €` : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {consultant.availability ? (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Disponible
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700">
                              <XCircle className="w-3 h-3 mr-1" />
                              En mission
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(consultant)}
                            data-testid={`edit-consultant-${consultant.consultant_id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Modifier le consultant</DialogTitle>
              <DialogDescription>
                {selectedConsultant?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="skills">Compétences</Label>
                <Input
                  id="skills"
                  placeholder="React, Node.js, Python (séparées par des virgules)"
                  value={editData.skills}
                  onChange={(e) => setEditData({ ...editData, skills: e.target.value })}
                  data-testid="edit-skills-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tjm">TJM (€)</Label>
                <Input
                  id="tjm"
                  type="number"
                  placeholder="500"
                  value={editData.tjm}
                  onChange={(e) => setEditData({ ...editData, tjm: e.target.value })}
                  data-testid="edit-tjm-input"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="availability">Disponible</Label>
                <Switch
                  id="availability"
                  checked={editData.availability}
                  onCheckedChange={(checked) => setEditData({ ...editData, availability: checked })}
                  data-testid="edit-availability-switch"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleSave} className="bg-slate-900 hover:bg-slate-800" data-testid="save-consultant-btn">
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
